import type { Mystery } from "../../types/game";
import { caseArtFor } from "./caseArt";

/* UI-DESIGN: the lightbox reads a small view model instead of the raw
   Mystery, so the gaps between the design and the API live in one place.
   See docs/lightbox/SPEC.md, "Data the frame shows that the API does not have". */

/* The live /games/mysteries response carries `mystery_sets` (one per player
   count), which the Mystery type does not declare yet. Read it defensively
   here; the type itself is Avi's and stays untouched. */
type MysterySet = { number_of_players?: number; image_url?: string };
type MysteryAsServed = Mystery & { mystery_sets?: MysterySet[] };

/* UI-DESIGN: LB-34 the old "2–3 hours" placeholder was a made-up number on
   every card. When the record has no length the field is left off the tag.
   TODO(Avi): how long does a game run? Either add a duration to the mystery
   record (preferred) or give one number here and it shows on every tag. */

export type CaseView = {
  id: string;
  number: string;
  name: string;
  players: string | null;
  /* null: the record carries no length, so the tag leaves the field off */
  length: string | null;
  premise: string;
  imageUrl: string | null;
};

const playersLabel = (m: MysteryAsServed): string | null => {
  const counts = (m.mystery_sets ?? [])
    .map((s) => s.number_of_players)
    .filter((n): n is number => typeof n === "number");
  if (m.number_of_players) counts.push(m.number_of_players);
  if (counts.length === 0) return null;
  const min = Math.min(...counts);
  const max = Math.max(...counts);
  return min === max ? `${min} players` : `${min}–${max} players`;
};

const firstSentence = (text: string | undefined): string => {
  if (!text) return "";
  const match = text.match(/^.*?[.!?](\s|$)/);
  return (match ? match[0] : text).trim();
};

const usableImage = (url: string | undefined): string | null => {
  if (!url) return null;
  /* example.com covers are seed placeholders, never real art */
  if (/^https?:\/\/(www\.)?example\.com\//.test(url)) return null;
  return url;
};

export const toCaseView = (mystery: Mystery, index: number): CaseView => {
  const m = mystery as MysteryAsServed;
  return {
    id: m.objectId,
    number: String(index + 1).padStart(2, "0"),
    name: m.name,
    players: playersLabel(m),
    length: m.number_of_rounds ? `${m.number_of_rounds} rounds` : null,
    premise: firstSentence(m.description),
    /* UI-DESIGN: LB-8 real API cover first, then the bundled case art */
    imageUrl: usableImage(m.image_url || m.mystery_sets?.[0]?.image_url) ?? caseArtFor(m.name),
  };
};

/* LOCAL FALLBACK: shown only when useGetMysteriesQuery errors (API down or
   unreachable in dev), and labelled on screen as preview data. Copy mirrors
   the live records as of 2026-09-28. */
export const FALLBACK_CASES: CaseView[] = [
  {
    id: "fallback-sherlocked",
    number: "01",
    name: "Sherlocked",
    players: "6–8 players",
    length: null,
    premise: "A classic detective mystery inspired by Sherlock-style intrigue.",
    imageUrl: caseArtFor("Sherlocked"),
  },
  {
    id: "fallback-white-lotus",
    number: "02",
    name: "White Lotus",
    players: "7–10 players",
    length: null,
    premise: "A luxury resort mystery full of secrets and suspicious guests.",
    imageUrl: caseArtFor("White Lotus"),
  },
  {
    id: "fallback-weekend-at-bernies",
    number: "03",
    name: "Weekend at Bernie's",
    players: "5–7 players",
    length: null,
    premise: "A dark comedy mystery where things are not what they seem.",
    imageUrl: caseArtFor("Weekend at Bernie's"),
  },
];
